import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Button, Grid, Typography } from "@mui/material"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CreateUpdateProductFormValues, DiscountType, OfferDetailsType } from "../../productsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { toDinero, toNumberLossy } from "../../../../../currency/currencyLib"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import { OfferDialog } from "./OfferDialog"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    button: {
        textTransform: "none",
        borderRadius: "0.5rem",
        padding: "0.125rem 0.75rem",
        color: "#2196F3",
        borderColor: "#2196F3",
    },
}))

type OfferDetailsProps = Readonly<{
    dialogState: boolean
    setDialogState: React.Dispatch<React.SetStateAction<boolean>>
    setCheckedOffer: React.Dispatch<React.SetStateAction<boolean>>
}>

export function OfferDetails({ setCheckedOffer, dialogState, setDialogState }: OfferDetailsProps) {
    const { values, setFieldValue } = useFormikContext<CreateUpdateProductFormValues>()
    const { offerDetails } = values
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const { classes } = useStyles()

    const toPercentage = (amount: number | undefined, scale: number | undefined) => {
        return (amount || 0) / Math.pow(10, scale || 0)
    }

    const initialValues = (offerDetailsItem?: OfferDetailsType) => {
        if (!offerDetailsItem) {
            return offerDetailsItem
        } else {
            const { discount, availabilityDetails } = offerDetailsItem

            const { percentage, price, ...restValues } = discount

            const dateTimeIntervalFrom = availabilityDetails?.dateTimeInterval?.from
                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.from)
                : undefined
            const dateTimeIntervalTo = availabilityDetails?.dateTimeInterval?.to
                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.to)
                : undefined

            const availabilityDetailsValue = {
                dateTimeInterval: {
                    from: dateTimeIntervalFrom,
                    to: dateTimeIntervalTo,
                },
            }

            if (discount.type === DiscountType.DISCOUNT_PERCENTAGE) {
                return {
                    ...offerDetailsItem,
                    discount: {
                        ...restValues,
                        percentage: {
                            amount: toPercentage(percentage?.amount, percentage?.scale).toString(),
                        },
                    },
                    availabilityDetails: availabilityDetailsValue,
                }
            }
            if (discount.type === DiscountType.DISCOUNT_PRICE) {
                return {
                    ...offerDetailsItem,
                    discount: {
                        ...restValues,
                        price: price ? toNumberLossy(toDinero(price)) : undefined,
                    },
                    availabilityDetails: availabilityDetailsValue,
                }
            }

            return {
                ...offerDetailsItem,
                discount: {
                    ...restValues,
                },
                availabilityDetails: availabilityDetailsValue,
            }
        }
    }

    return (
        <>
            {offerDetails && (
                <>
                    <Grid container gap={1} flexDirection="column" width="100%" className={classes.title}>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            <LocalizedStrict id="product-create-offer-discount">
                                <Typography variant="subtitle2">Discount:</Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle2">
                                {offerDetails.discount.type === DiscountType.DISCOUNT_PERCENTAGE
                                    ? offerDetails.discount.percentage
                                        ? `${toPercentage(
                                              offerDetails.discount.percentage.amount,
                                              offerDetails.discount.percentage.scale,
                                          )}%`
                                        : "-"
                                    : offerDetails.discount.price
                                      ? moneyFactory.format(toDinero(offerDetails.discount.price))
                                      : "-"}
                            </Typography>
                        </Grid>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            <LocalizedStrict id="product-create-offer-available-from">
                                <Typography variant="subtitle2">Available from:</Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle2">
                                {offerDetails.availabilityDetails?.dateTimeInterval?.from
                                    ? dateTimeFactory.formatDateTime(
                                          dateTimeFactory.fromApi(
                                              offerDetails.availabilityDetails.dateTimeInterval.from,
                                          ),
                                      )
                                    : "-"}
                            </Typography>
                        </Grid>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            <LocalizedStrict id="product-create-offer-available-to">
                                <Typography variant="subtitle2">Available to:</Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle2">
                                {offerDetails.availabilityDetails?.dateTimeInterval?.to
                                    ? dateTimeFactory.formatDateTime(
                                          dateTimeFactory.fromApi(offerDetails.availabilityDetails.dateTimeInterval.to),
                                      )
                                    : "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end">
                        <LocalizedStrict id="product-create-offer-edit-button">
                            <Button
                                onClick={() => setDialogState(!dialogState)}
                                color="primary"
                                variant="outlined"
                                startIcon={<FontAwesomeIcon icon={faPen} />}
                                size="small"
                                className={classes.button}
                            >
                                Edit
                            </Button>
                        </LocalizedStrict>
                    </Grid>
                </>
            )}
            {dialogState && (
                <OfferDialog
                    open={dialogState}
                    onClose={() => setDialogState(false)}
                    initialValues={initialValues(offerDetails)}
                    handleSaveOffer={(data) => {
                        setFieldValue("offerDetails", data)
                        setCheckedOffer(true)
                    }}
                />
            )}
        </>
    )
}
